'use strict'

import { DOM } from './../ui/DOM.js'
import { Selector } from './../ui/Selectors.js'
import Dropcolor from './../Dropcolor.js'
import SwatchPanel from '../ui/visualizer-swatch-panel.js'
import TextureSwatchPanel, { updateTextureSwatchBackgroundColor } from '../ui/visualizer-texture-swatch-panel.js'
import Utils from './../Utils.js'
import { TOOL } from './../constants/Tools.js'
import { TOOL_BUTTON_ID, TOOL_BUTTON_SELECTOR } from './../constants/ToolButtons.js'

/**
 * PaintingUIBinder
 * Owns all DOM/event wiring for the painting UI and delegates logic via callbacks.
 * Keeps wiring concerns separate from the tool/drop/save/export handlers.
 */
class PaintingUIBinder {
    /**
     * @param {Object} opts - Wiring options.
     * @param {Object} opts.application - Visualizer Application instance.
     * @param {Document|HTMLElement} opts.root - Root scope for DOM queries.
     * @param {(toolName: string) => void} opts.onToolSelect - Tool selection callback.
     * @param {() => void} opts.onExportJpeg - JPEG export callback.
     * @param {() => void} opts.onExportPdf - PDF export callback.
     * @param {(nameInput: HTMLInputElement|null) => void} opts.onExportProject - Project export callback.
     * @param {() => Promise<void>|void} opts.onSave - Save flow callback.
     * @param {(el: HTMLElement) => void} opts.onSlotClick - Slot click callback.
     * @param {(event: Event, ui: Object, id: string) => void} opts.onSlotDrop - Slot drop callback.
     * @param {(ui: Object) => void} opts.onCanvasDrop - Canvas drop callback.
     * @param {() => void} opts.onLoginRequired - Called when login is needed.
     * @param {() => void} opts.onVerifyRequired - Called when verification is needed.
     * @param {Object} opts.toolActions - Pointer handler for drawing tools.
     * @param {() => void} [opts.onBuy] - Optional CTA handler.
     * @param {(event: Event) => void} [opts.onDeleteSlot] - Optional delete-slot callback.
     */
    constructor(opts) {
        this.app = opts.application
        this.root = opts.root
        this.onToolSelect = opts.onToolSelect
        this.onExportJpeg = opts.onExportJpeg
        this.onExportPdf = opts.onExportPdf
        this.onExportProject = opts.onExportProject
        this.onSave = opts.onSave
        this.onSlotClick = opts.onSlotClick
        this.onSlotDrop = opts.onSlotDrop
        this.onCanvasDrop = opts.onCanvasDrop
        this.onLoginRequired = opts.onLoginRequired
        this.onVerifyRequired = opts.onVerifyRequired
        this.toolActions = opts.toolActions
        this.onBuy = opts.onBuy
        this.onDeleteSlot = opts.onDeleteSlot
    }

    /** Cache frequently accessed DOM nodes (buttons, selects, sliders, canvas). */
    wireCoreSelectors() {
        this.$view = DOM.get(Selector.canvasMain)
        this.$name = DOM.get(Selector.nameInput)
        this.$btnUndo = DOM.get(Selector.btnUndo)
        this.$btnRedo = DOM.get(Selector.btnRedo)
        this.$btnPDF = DOM.get(Selector.btnPDF)
        this.$btnExport = DOM.get(Selector.btnExport)
        this.$btnNewProject = DOM.get(Selector.btnNewProject)
        this.$btnJpeg = DOM.get(Selector.btnJpeg)
        this.$selectCc = DOM.get(Selector.selectCc)
        this.$selectTe = DOM.get(Selector.selectTe)
        this.$sliderBrush = DOM.get(Selector.brushSizeSlider)
        this.$sliderWheel = DOM.get(Selector.sliderWheelAlt)
    }

    /** Wire tool buttons (brush/eraser/etc.) and undo/redo handlers. */
    wireToolButtons() {
        const app = this.app

        const selectTool = (tool) => {
            this.onToolSelect(tool)
            this.applyToolUi(tool)
        }

        DOM.get(Selector.btnBrush)?.addEventListener('click', () => {
            selectTool(TOOL.BRUSH)
        })

        DOM.get(Selector.btnEraser)?.addEventListener('click', () => {
            selectTool(TOOL.ERASER)
        })

        DOM.get(Selector.btnMagic)?.addEventListener('click', () => {
            selectTool(TOOL.MAGIC)
        })

        DOM.get(Selector.btnHand)?.addEventListener('click', () => {
            selectTool(TOOL.HAND)
        })

        DOM.get(Selector.btnDrawLine)?.addEventListener('click', () => {
            selectTool(TOOL.DRAW_LINE)
        })

        this.root.querySelectorAll(`${TOOL_BUTTON_SELECTOR.DRAW_POLY_MINUS}, ${TOOL_BUTTON_SELECTOR.DRAW_POLY_PLUS}`).forEach((btn) => {
            btn.addEventListener('click', (evt) => {
                const id = evt.currentTarget.id
                const tool =
                    id === TOOL_BUTTON_ID.DRAW_POLY_MINUS
                        ? TOOL.DRAW_POLY_MINUS
                        : id === TOOL_BUTTON_ID.DRAW_POLY_PLUS
                          ? TOOL.DRAW_POLY_PLUS
                          : null
                if (!tool) return
                selectTool(tool)
            })
        })

        DOM.get(Selector.btnRotate)?.addEventListener('click', () => {
            app.canvasState.rotate(app.toolManager)
            DOM.hide(Selector.brushSizeSlider)
            DOM.hide(Selector.sliderWheelAlt)
        })

        this.$btnUndo?.addEventListener('mouseup', (e) => {
            e.preventDefault()
            app.canvasState.undo()
        })
        this.$btnRedo?.addEventListener('mouseup', (e) => {
            e.preventDefault()
            app.canvasState.redo()
        })
    }

    /**
     * Apply tool-specific UI state (cursor + slider visibility).
     *
     * This is the single place that controls slider visibility so both
     * click handlers and programmatic tool switches stay consistent.
     *
     * @param {string} tool - Tool name (brush, eraser, hand, etc.).
     */
    applyToolUi(tool) {
        const app = this.app

        if (tool === TOOL.BRUSH || tool === TOOL.ERASER) {
            this._setCursor(
                `${app.assetPath}/img/icons/cursorcircle${app.toolManager.brushSize}.png`,
                app.toolManager.brushSize / 2,
                app.toolManager.brushSize / 2,
            )
            DOM.show(Selector.brushSizeSlider)
            DOM.hide(Selector.sliderWheelAlt)
            app.toolManager.sliderBrushSize()
            app.toolManager.sliderBrushCursorIcon()
            return
        }

        if (tool === TOOL.MAGIC) {
            this._setCursor(`${app.assetPath}/img/icons/scopecursor.png`, 12, 12)
            DOM.hide(Selector.brushSizeSlider)
            DOM.show(Selector.sliderWheelAlt)
            return
        }

        if (tool === TOOL.DRAW_LINE || tool === TOOL.DRAW_POLY_MINUS || tool === TOOL.DRAW_POLY_PLUS) {
            this._setPrecisionCrosshairCursor()
            DOM.hide(Selector.brushSizeSlider)
            DOM.hide(Selector.sliderWheelAlt)
            return
        }

        if (tool === TOOL.HAND) {
            DOM.hide(Selector.brushSizeSlider)
            DOM.hide(Selector.sliderWheelAlt)
        }
    }

    /** Wire color and texture selectors to update swatch data and toggle UI. */
    wirePickers() {
        this._swatchPanel = new SwatchPanel({
            app: this.app,
            selectEl: this.$selectCc,
        })
        this._swatchPanel.bind()

        this._textureSwatchPanel = new TextureSwatchPanel({
            app: this.app,
            selectEl: this.$selectTe,
        })
        this._textureSwatchPanel.bind()
    }

    /**
     * Wire export buttons (JPEG/PDF/project) plus New Project save flow with auth gates.
     */
    wireExporters() {
        this.$btnJpeg?.addEventListener('click', () => this.onExportJpeg())
        this.$btnPDF?.addEventListener('click', () => this.onExportPdf())
        this.$btnExport?.addEventListener('click', () => this.onExportProject(this.$name))

        this.$btnNewProject?.addEventListener('click', async () => {
            const app = this.app
            // Save is an authenticated action: guests must always go through login.
            if (!app.user_logged_in) {
                this.onLoginRequired?.()
                return
            }

            // Optional-registration simulators gate save behind passcode verification.
            if (app.application_verification_code && !app.user_verified) {
                this.onVerifyRequired?.()
                return
            }

            await this.onSave()
        })
    }

    /** Wire pointer events for the main canvas (mouse + touch). */
    wireCanvasPointerEvents() {
        const view = this.$view
        if (!view) return

        view.addEventListener('mousedown', (e) => {
            e.preventDefault()
            const c = Utils.getMousePos(view, e)
            this.toolActions.onMouseDown(c.x, c.y)
        })
        view.addEventListener(
            'touchstart',
            (e) => {
                e.preventDefault()
                const c = Utils.getTouchPos(view, e)
                this.toolActions.onMouseDown(c.x, c.y)
            },
            { passive: false },
        )

        view.addEventListener('mousemove', (e) => {
            e.preventDefault()
            const c = Utils.getMousePos(view, e)
            this.toolActions.onMouseMove(c.x, c.y)
        })
        view.addEventListener(
            'touchmove',
            (e) => {
                e.preventDefault()
                const c = Utils.getTouchPos(view, e)
                this.toolActions.onMouseMove(c.x, c.y)
            },
            { passive: false },
        )

        view.addEventListener('mouseup', (e) => {
            e.preventDefault()
            this.toolActions.onMouseUp()
        })
        view.addEventListener(
            'touchend',
            (e) => {
                e.preventDefault()
                this.toolActions.onMouseUp()
            },
            { passive: true },
        )
    }

    /** Wire drag/drop handlers for canvas and dropcolor slots. */
    wireDropHandlers() {
        this.root.querySelectorAll('.dropcolor').forEach((el) => {
            ;['dragenter', 'dragstart', 'dragend', 'dragleave', 'dragover', 'drag', 'drop'].forEach((t) =>
                el.addEventListener(t, (e) => e.preventDefault()),
            )
        })

        $(Selector.canvasMain).droppable({
            over: (event, ui) => $(ui.helper).addClass('ui-draggable-helper'),
            out: (event, ui) => $(ui.helper).removeClass('ui-draggable-helper'),
            accept: '.js-draggable, .js-draggable-drag',
            hoverClass: 'ui-state-active-imageview',
            drop: (event, ui) => this.onCanvasDrop(ui),
        })

        $('.dropcolor').each((_, el) => {
            $(el).droppable({
                accept: '.js-draggable, .js-draggable-drag',
                hoverClass: 'ui-state-active',
                classes: {
                    'ui-droppable-active': 'ui-state-dropcolor',
                    'ui-droppable-hover': 'ui-state-dropcolor-hover',
                },
                drop: (event, ui) => {
                    const droppableId = el.id
                    this.onSlotDrop(event, ui, droppableId)
                },
            })
        })

        this.root.querySelectorAll('.dropcolor').forEach((el) => {
            el.addEventListener('click', () => this.onSlotClick(el))
            el.addEventListener('dblclick', () => {
                const slot = parseInt(el.id.replace('dropcolor', ''), 10)
                if (!slot) return
                document.dispatchEvent(
                    new CustomEvent('viz:dropcolorDetailsRequested', {
                        detail: { slot },
                    }),
                )
            })
        })

        $(document)
            .off('click.paintinglistener', Selector.deletedDropcolorButtons)
            .on('click.paintinglistener', Selector.deletedDropcolorButtons, (event) => this.onDeleteSlot?.(event))
    }

    /** Misc wiring: touch bridging, swatch tile selection, and optional buy CTA. */
    wireMisc() {
        this._addTouchEventToDropColor(this.root)

        this.root.addEventListener(
            'click',
            async (e) => {
                const tile = e.target.closest(`.${Dropcolor.CLASS_CLICKABLE_TILE}`)
                if (!tile) return

                const id = tile.id || tile.parentElement?.id
                if (!id) return

                const isTexture =
                    tile.classList.contains(Dropcolor.CLASS_TEXTURE) || tile.classList.contains(Dropcolor.CLASS_TEXTURE_SWATCH)

                if (isTexture && tile.classList.contains(Dropcolor.CLASS_TEXTURE_SWATCH)) {
                    document
                        .querySelectorAll('.texture-swatch.textureSwatchSelected')
                        .forEach((el) => el.classList.remove('textureSwatchSelected'))
                    tile.classList.add('textureSwatchSelected')
                }

                if (!isTexture) {
                    const bg = getComputedStyle(tile).backgroundColor
                    const match = bg.match(/^rgba?\(\s*([0-9]+)\s*[, ]\s*([0-9]+)\s*[, ]\s*([0-9]+)\b/i)
                    if (match) {
                        const r = parseInt(match[1], 10)
                        const g = parseInt(match[2], 10)
                        const b = parseInt(match[3], 10)

                        updateTextureSwatchBackgroundColor({
                            r,
                            g,
                            b,
                            swatch_color_id: id,
                        })
                    }
                }

                const slot = this.app.canvasState.colorSample.workingColorPos
                if (!slot) return

                document.dispatchEvent(
                    new CustomEvent('viz:swatchColorSelected', {
                        detail: { slot, tileId: id },
                    }),
                )
            },
            true,
        )

        DOM.get(Selector.btnAchat)?.addEventListener('click', () => this.onBuy?.())
    }

    /**
     * Translate touch events to mouse events for draggable swatches.
     * @param {Document|HTMLElement} [root=document] - Scope for draggable tiles.
     * @private
     */
    _addTouchEventToDropColor(root = document) {
        const bridge = (collection) => {
            for (const el of Array.from(collection)) {
                const tgt = el.firstElementChild || el
                tgt.addEventListener('touchstart', this._touchToMouseTile, { passive: false })
                tgt.addEventListener('touchmove', this._touchToMouseTile, { passive: false })
                tgt.addEventListener('touchend', this._touchToMouseTile, { passive: true })
            }
        }
        bridge(root.getElementsByClassName('cbox-ratio-color'))
        bridge(root.getElementsByClassName('cbox-ratio-texture'))
    }

    /**
     * Translate touch events on swatches to mouse events for compatibility with jQuery UI draggable.
     * @param {TouchEvent} e - Native touch event.
     * @private
     */
    _touchToMouseTile(e) {
        const map = { touchstart: 'mousedown', touchmove: 'mousemove', touchend: 'mouseup' }
        const type = map[e.type]
        if (!type) return

        for (const t of e.changedTouches) {
            const target = document.elementFromPoint(t.clientX, t.clientY) || e.target
            const ev = new MouseEvent(type, {
                bubbles: true,
                cancelable: true,
                view: window,
                detail: 1,
                screenX: t.screenX,
                screenY: t.screenY,
                clientX: t.clientX,
                clientY: t.clientY,
                ctrlKey: e.ctrlKey,
                altKey: e.altKey,
                shiftKey: e.shiftKey,
                metaKey: e.metaKey,
                button: 0,
                buttons: 1,
            })
            target.dispatchEvent(ev)
        }

        e.preventDefault()
        e.stopPropagation()
    }

    /**
     * Set a custom cursor on the main canvas.
     * @param {string} url - Cursor image URL.
     * @param {number} x - Hotspot x coordinate.
     * @param {number} y - Hotspot y coordinate.
     * @private
     */
    _setCursor(url, x, y) {
        $(Selector.canvasMain).css({ cursor: `url(${url}) ${x} ${y}, auto` })
    }

    /**
     * Set a high-visibility precision cursor (crosshair + center dot) for tools
     * that place points/segments (line + polygon +/-). The center dot makes the
     * exact hit point explicit while keeping a crosshair fallback.
     * @private
     */
    _setPrecisionCrosshairCursor() {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 1v7M12 16v7M1 12h7M16 12h7" stroke="black" stroke-width="2" stroke-linecap="round"/><path d="M12 2v6M12 16v6M2 12h6M16 12h6" stroke="white" stroke-width="1" stroke-linecap="round"/><circle cx="12" cy="12" r="2.2" fill="white" stroke="black" stroke-width="1.2"/></svg>`
        const encoded = encodeURIComponent(svg)
        $(Selector.canvasMain).css({ cursor: `url("data:image/svg+xml,${encoded}") 12 12, crosshair` })
    }
}

export default PaintingUIBinder
